import {
  Collapse,
  Link,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Info } from '@material-ui/icons';
import { formatDate, NullableDateString } from '@superdispatch/dates';
import { FormikDateField, FormikTextField } from '@superdispatch/forms';
import { Column, Columns, Inline, Stack } from '@superdispatch/ui';
import { useField } from 'formik';
import { joinStrings } from 'shared/utils/StringUtils';
import { loadVenueSchema } from '../data/LoadDTO';
import { FormikVenueAddressAutofill } from './FormikVenueAddressAutofill';
import {
  FormikVenueAddressSuggestion,
  useVenueAddressState,
} from './FormikVenueAddressSuggestion';
import { VenueAddressVerificationProvider } from './VenueAddressVerificationContext';
import { FormikVenueAutofill } from './FormikVenueAutofill';
import { Box } from '@superdispatch/ui-lab';
import { LocationProtectedIcon } from 'shared/icons/LocationProtectedIcon';
import { useFlag } from 'shared/settings/FeatureToggles';

interface LoadPickupFormProps {
  name?: string;
  disabled?: boolean;
  isEDI?: boolean;
  isDisabledAddressDetails?: boolean;
  isSubhauled?: boolean | null;
  canEditAddress?: boolean | null;
  isPrimeCarrierSubhaulLoadEditPage?: boolean;
}

export function LoadPickupForm({
  name,
  isSubhauled = false,
  disabled,
  isDisabledAddressDetails,
  canEditAddress = true,
  isPrimeCarrierSubhaulLoadEditPage,
  isEDI = false,
}: LoadPickupFormProps) {
  const prefix = !name ? '' : `${name}.`;
  const { verifiedAddress, setVerifiedAddress } = useVenueAddressState(
    `${prefix}venue`,
  );
  const [{ value: actualDate }] = useField<NullableDateString>(
    `${prefix}actual_date`,
  );
  const isRestrictedAddressEditing = useFlag(
    'carrier_restrict_shipper_address_editing',
  );
  const isAllowedToEditBusinessName = useFlag(
    'carrier_allow_business_name_updating',
  );

  const isBusinessNameDisabled = isAllowedToEditBusinessName
    ? disabled || isDisabledAddressDetails || isEDI
    : disabled || !canEditAddress || isDisabledAddressDetails || isEDI;

  return (
    <Stack space="large">
      {isAllowedToEditBusinessName && !canEditAddress ? (
        <FormikTextField
          fullWidth={true}
          label="Business Name"
          name={`${prefix}venue.name`}
          disabled={isBusinessNameDisabled}
        />
      ) : (
        <FormikVenueAutofill
          fullWidth={true}
          label="Business Name"
          name={`${prefix}venue`}
          schema={loadVenueSchema}
          disabled={isBusinessNameDisabled}
        />
      )}

      <VenueAddressVerificationProvider
        name={`${prefix}venue`}
        verifiedAddress={verifiedAddress}
        onVerify={setVerifiedAddress}
      >
        <Stack space="small">
          <FormikVenueAddressAutofill
            fullWidth={true}
            label="Street Address"
            name={`${prefix}venue`}
            disabled={
              disabled ||
              !canEditAddress ||
              isDisabledAddressDetails ||
              !!isPrimeCarrierSubhaulLoadEditPage ||
              isEDI
            }
            onGeocode={(geocoding) => {
              setVerifiedAddress(
                joinStrings(' ', geocoding.house_number, geocoding.address),
              );
            }}
          />

          <Columns space="small" collapseBelow="desktop">
            <Column width="1/2">
              <FormikTextField
                fullWidth={true}
                label="City"
                name={`${prefix}venue.city`}
                disabled={
                  disabled ||
                  !canEditAddress ||
                  isDisabledAddressDetails ||
                  !!isPrimeCarrierSubhaulLoadEditPage ||
                  isEDI
                }
              />
            </Column>

            <Column width="1/4">
              <FormikTextField
                fullWidth={true}
                label="State"
                name={`${prefix}venue.state`}
                disabled={
                  disabled ||
                  !canEditAddress ||
                  isDisabledAddressDetails ||
                  !!isPrimeCarrierSubhaulLoadEditPage ||
                  isEDI
                }
              />
            </Column>

            <Column width="1/4">
              <FormikTextField
                fullWidth={true}
                label="ZIP"
                name={`${prefix}venue.zip`}
                disabled={
                  disabled ||
                  !canEditAddress ||
                  isDisabledAddressDetails ||
                  !!isPrimeCarrierSubhaulLoadEditPage ||
                  isEDI
                }
              />
            </Column>
          </Columns>

          <Collapse in={!canEditAddress && !isSubhauled} unmountOnExit={true}>
            <Box padding="xsmall" borderRadius="small" backgroundColor="Blue50">
              {isRestrictedAddressEditing ? (
                <Columns space="xsmall" align="center">
                  <Column width="content">
                    <Info fontSize="small" color="primary" />
                  </Column>
                  <Column width="fluid">
                    <Typography>
                      Location set by the shipper cannot be changed. Contact
                      shipper to update.
                    </Typography>
                  </Column>
                </Columns>
              ) : (
                <Columns space="xsmall">
                  <Column width="content">
                    <Box paddingTop="xxsmall">
                      <LocationProtectedIcon fontSize="small" color="primary" />
                    </Box>
                  </Column>
                  <Column width="fluid">
                    <Typography>
                      Pickup location can’t be changed because this load
                      includes one or more location-protected attachments.{' '}
                      <Link
                        href="https://support.superdispatch.com/hc/en-us/articles/47849234437267-How-Gate-Pass-Protection-Works-in-the-Driver-App-and-Carrier-TMS"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Learn more
                      </Link>
                    </Typography>
                  </Column>
                </Columns>
              )}
            </Box>
          </Collapse>
          <FormikVenueAddressSuggestion />
        </Stack>
      </VenueAddressVerificationProvider>

      <Columns space="small" collapseBelow="desktop">
        <Column width="1/2">
          <FormikDateField
            fullWidth={true}
            label="Scheduled Pickup Date"
            name={`${prefix}scheduled_date`}
            disabled={
              disabled ||
              !!isSubhauled ||
              isDisabledAddressDetails ||
              !!isPrimeCarrierSubhaulLoadEditPage
            }
          />
        </Column>

        {actualDate && (
          <Column width="1/2">
            <TextField
              fullWidth={true}
              InputProps={{ readOnly: true }}
              value={formatDate(actualDate, { variant: 'DateTime' })}
              disabled={
                disabled ||
                !!isSubhauled ||
                isDisabledAddressDetails ||
                !!isPrimeCarrierSubhaulLoadEditPage ||
                isEDI
              }
              label={
                <Inline verticalAlign="center">
                  <Typography>Picked Up on</Typography>
                  <Tooltip
                    enterTouchDelay={0}
                    placement="top"
                    title={`The date when the load was picked
                            up by the driver or marked as picked
                            up by a dispatcher. Cannot be
                            changed.`}
                  >
                    <Info color="action" fontSize="small" />
                  </Tooltip>
                </Inline>
              }
            />
          </Column>
        )}
      </Columns>

      <Columns space="small" collapseBelow="desktop">
        <Column width="1/2">
          <FormikTextField
            fullWidth={true}
            label="Buyer Number"
            name={`${prefix}venue.buyer_number`}
            disabled={
              disabled || !!isSubhauled || !!isPrimeCarrierSubhaulLoadEditPage
            }
          />
        </Column>
      </Columns>

      <FormikTextField
        label="Notes"
        name={`${prefix}notes`}
        fullWidth={true}
        multiline={true}
        disabled={disabled}
      />

      <Stack space="small">
        <Typography variant="h4">Pickup Contact Details</Typography>

        <FormikTextField
          fullWidth={true}
          label="Full Name"
          name={`${prefix}venue.contacts[0].name`}
          disabled={disabled}
        />

        <Columns space="small" collapseBelow="desktop">
          <Column width="1/2">
            <FormikTextField
              fullWidth={true}
              label="Phone"
              name={`${prefix}venue.contacts[0].phone`}
              disabled={disabled}
            />
          </Column>
          <Column width="1/2">
            <FormikTextField
              fullWidth={true}
              label="Mobile"
              name={`${prefix}venue.contacts[0].phone2`}
              disabled={disabled}
            />
          </Column>
        </Columns>
        <FormikTextField
          fullWidth={true}
          label="Email"
          name={`${prefix}venue.contacts[0].email`}
          disabled={disabled}
        />
      </Stack>
    </Stack>
  );
}
